import { useContext, useEffect, useState } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Context as AppContext } from '../../context/AppContext';

const favouritesQuery = gql`
  query favourites($userId: String!, $after: String) {
    favourites(userId: $userId, after: $after) {
      edges {
        node {
          listing {
            id
            listingAssets {
              url
            }
            listingLocations {
              suburb {
                name
                postcode
                state
              }
            }
            name
            reviewAggregates {
              averageRating
            }
            vanityUrlPath
          }
        }
      }
    }
  }
`;

/**
 * The `favourites` graphQL query currently returns favourites as an array of Listings rather than Profiles.
 * This function allows for converting the Listings to Profiles format. Only fields required for favourites functionality are converted.
 * Accepts a single Listing and returns a single Profile
 * @param {Object} listing
 * @param {string} listing.id
 * @param {Array} listing.listingAssetes
 * @param {string} listing.name
 * @param {Object} listing.reviewAggregates
 * @param {string} listing.vanityUrlPath
 * @returns {Object} A profile
 */
const transformListing = listing => {
  const {
    id,
    listingAssets,
    listingLocations,
    name,
    reviewAggregates,
    vanityUrlPath,
  } = listing;

  const location =
    listingLocations && listingLocations.length
      ? listingLocations[0]
      : undefined;

  const getAddress = () => {
    return location && location.suburb
      ? {
          suburb: location.suburb.name,
          postcode: location.suburb.postcode,
          stateCode: location.suburb.state,
        }
      : {};
  };

  return {
    displayName: name,
    id,
    media: listingAssets,
    reviewAggregates,
    vanityUrlPath,
    ...getAddress(),
  };
};

/**
 * Fetches user favourites and returns an object containing the array of favourites (as Profiles) and the loading state
 * @returns {Object}
 */
const useFetchFavourites = () => {
  const [loadingState, setLoadingState] = useState(false);
  const { user: { user: { id: userId } = {} } = {} } =
    useContext(AppContext) || {};

  const [fetchFavourites, { data, loading }] = useLazyQuery(favouritesQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  const { favourites: { edges = [] } = {} } = data || {};
  const favourites = edges
    .filter(({ node: { listing } }) => listing)
    .map(({ node: { listing } }) => transformListing(listing));

  useEffect(() => {
    if (userId) {
      if (!loadingState) {
        fetchFavourites({ variables: { userId } });
      }
      setLoadingState(loading);
    }
  }, [userId, loading]);

  return { favourites, loading: loadingState };
};

export default useFetchFavourites;
